/** "History" price popup on a line: category + words → P25 / median / P75 and the 200 cheapest records ($0 excluded, 偏差 D2). */
import { useEffect, useState } from "react";
import { applyLookup, f3, n, type PriceRec } from "@ic/engine";
import { useStore } from "../../store.ts";
import { useT } from "../../shared/i18n.ts";
import { useMoney } from "../../shared/money.ts";
import { api } from "../../shared/api.ts";
import { Modal } from "../../shared/ui/common.tsx";

export function Lookup({ line, i }: { line: "fab" | "trim"; i: number }) {
  const t = useT();
  const m = useMoney(); // prices in the currency picked at the top (JF 2026-10-13)
  const lang = useStore((s) => s.lang);
  const meta = useStore((s) => s.meta);
  const r = useStore((s) => s.sheet[line][i]);
  const { closeModal, setSheet, toast } = useStore.getState();
  const [cat, setCat] = useState(r?.cat || "");
  const [q, setQ] = useState(r?.cat ? "" : (r?.name || "").trim());
  const [res, setRes] = useState<Awaited<ReturnType<typeof api.prices>> | null>(null);
  useEffect(() => {
    const tm = setTimeout(() => { api.prices(line, cat, q).then(setRes).catch((e) => toast(String(e.message || e))); }, res ? 200 : 0);
    return () => clearTimeout(tm);
  }, [cat, q]); // eslint-disable-line react-hooks/exhaustive-deps
  if (!r) return null;
  const cats = (line === "fab" ? meta?.fabricCategories : meta?.trimCategories) || [];
  const use = (x: PriceRec, withName: boolean) => {
    setSheet(applyLookup(useStore.getState().sheet, line, i, x, res?.stats ?? null, withName, lang));
    closeModal(); toast(t("Historical price applied", "已采用历史价格"));
  };
  return (
    <Modal onClose={closeModal} wide>
      <h3>{t("Historical prices", "历史价格")} — {r.name || (line === "fab" ? t("fabric", "面料") : t("trim", "辅料"))}</h3>
      <div className="sgrid" style={{ gridTemplateColumns: "1fr 2fr auto" }}>
        <div className="fl"><label>{t("Category", "类别")}</label>
          <select className="in" id="lkC" value={cat} onChange={(e) => setCat(e.target.value)}><option value="">{t("All categories", "全部类别")}</option>{cats.map((c) => <option key={c}>{c}</option>)}</select></div>
        <div className="fl"><label>{t("Search code / description / SC", "搜索料号 / 描述 / SC")}</label><input className="in" id="lkQ" value={q} onChange={(e) => setQ(e.target.value)} /></div>
        <button className="btn" onClick={closeModal}>{t("Close", "关闭")}</button>
      </div>
      <div className="stats" id="lkStats">{res && (res.stats ? <>
        <span>{res.total} {t("records", "条记录")}</span><span>P25 <b>{m.sym}{f3(n(res.stats.p25) * m.k)}</b></span><span>{t("Median", "中位数")} <b>{m.sym}{f3(n(res.stats.med) * m.k)}</b></span><span>P75 <b>{m.sym}{f3(n(res.stats.p75) * m.k)}</b></span>
      </> : <span>{t("No records", "没有记录")}</span>)}</div>
      <div id="lkList" style={{ maxHeight: "60vh", overflow: "auto" }}>
        <table className="t"><tbody>
          <tr><th className="num">{t("Unit $", "单价 $")}</th><th>{t("Category", "类别")}</th><th>{t("Code", "料号")}</th><th>{t("Description", "描述")}</th><th>SC</th><th></th></tr>
          {res?.recs.map((x, j) => (
            <tr className="rec" key={j}>
              <td className="num">{f3(n(x.px) * m.k)}{x.unit && <span className="dim">/{x.unit}</span>}</td><td>{x.c}</td><td className="mono">{x.code}</td><td>{String(x.d).slice(0, 120)}</td><td className="mono">{x.sc}</td>
              <td style={{ whiteSpace: "nowrap" }}><button className="btn sm" data-use={j} onClick={() => use(x, false)}>{t("Use price", "用这个价")}</button>{" "}
                <button className="btn sm" data-usen={j} onClick={() => use(x, true)}>{t("Price + name", "价格 + 名称")}</button></td>
            </tr>
          ))}
        </tbody></table>
        {res && res.total > res.recs.length && <div className="dim">{t("Showing the 200 cheapest — refine the search.", "只显示最便宜的 200 条，请细化搜索。")}</div>}
      </div>
    </Modal>
  );
}
